import { useQuery } from "@tanstack/react-query";
import { get } from "./client";

// Market overview
export function useMarketOverview() {
  return useQuery({
    queryKey: ["marketOverview"],
    queryFn: () => get("/market/overview"),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Stocks list by market
export function useStocks({ market, limit = 100 }) {
  const query = market ? `?market=${market}` : "";
  const lim = limit ? `${query ? "&" : "?"}limit=${limit}` : "";
  return useQuery({
    queryKey: ["stocks", market || "all", limit],
    queryFn: () => get(`/market/stocks${query}${lim}`),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Stock by symbol with market
export function useStockDetail({ symbol, market }) {
  const m = market ? `?market=${market}` : "";
  return useQuery({
    queryKey: ["stockDetail", symbol, market],
    queryFn: () => get(`/market/stocks/${encodeURIComponent(symbol)}${m}`),
    enabled: !!symbol,
    staleTime: 30 * 1000,
  });
}

// Historical OHLC (full history by default)
export function useHistorical({ symbol, market } = {}) {
  const params = new URLSearchParams();
  if (market) params.append("market", market);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return useQuery({
    queryKey: ["historical", symbol, market || ""],
    queryFn: () => get(`/market/historical/${encodeURIComponent(symbol)}${suffix}`),
    enabled: !!symbol,
    staleTime: 60 * 1000,
  });
}

// ROI by symbol, requires market/category to target correct cluster
export function useROI({ symbol, market }) {
  const params = new URLSearchParams();
  if (market) params.append("market", market);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return useQuery({
    queryKey: ["roi", symbol, market || ""],
    queryFn: () => get(`/metrics/roi/${encodeURIComponent(symbol)}${suffix}`),
    enabled: !!symbol && !!market,
    staleTime: 5 * 60 * 1000,
  });
}

// Trade signals (BUY/SELL) by symbol, optional model filter
export function useTradeSignals({ symbol, model } = {}) {
  const params = new URLSearchParams();
  if (model) params.append("model", model);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return useQuery({
    queryKey: ["tradeSignals", symbol, model || ""],
    queryFn: () => get(`/market/trade-signals/${encodeURIComponent(symbol)}${suffix}`),
    enabled: !!symbol,
    staleTime: 5 * 60 * 1000,
  });
}

// Search
export function useSearch({ q, limit = 20 }) {
  const query = `?q=${encodeURIComponent(q)}&limit=${limit}`;
  return useQuery({
    queryKey: ["search", q, limit],
    queryFn: () => get(`/market/search${query}`),
    enabled: (q || "").length >= 2,
  });
}

// Predictions for a symbol with optional model filter
export function usePredictions({ symbol, market, model, limit = 50 }) {
  const params = new URLSearchParams();
  if (market) params.append("market", market);
  if (model) params.append("model", model);
  if (limit) params.append("limit", limit);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return useQuery({
    queryKey: ["predictions", symbol, market, model, limit],
    queryFn: () => get(`/market/predictions/${encodeURIComponent(symbol)}${suffix}`),
    enabled: !!symbol && !!market,
    staleTime: 5 * 60 * 1000,
  });
}

// Grouped syminfo for left-pane stock list
export function useGroupedSyminfo({ limitPerCategory } = {}) {
  const suffix = limitPerCategory ? `?limit_per_category=${limitPerCategory}` : "";
  return useQuery({
    queryKey: ["groupedSyminfo", limitPerCategory || "all"],
    queryFn: () => get(`/market/syminfo/grouped${suffix}`),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Daily signals from overview _AGGREGATE docs
export function useDailySignals({ categories } = {}) {
  const params = new URLSearchParams();
  if (Array.isArray(categories) && categories.length) {
    params.set("categories", categories.join(","));
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return useQuery({
    queryKey: ["dailySignals", ...(categories || ["default"])],
    queryFn: () => get(`/market/daily-signals${suffix}`),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}